import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Mail,
  Send,
  CheckCheck,
  Eye,
  MousePointer,
  Search,
  ChevronDown,
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '../components/Card';
import { Button } from '../components/Button';
import { EmailStatusBadge } from '../components/EmailStatusBadge';
import { AwaitingResponseList } from '../components/AwaitingResponseList';
import { EmailDetailDrawer } from '../components/EmailDetailDrawer';
import { emailsApi } from '../lib/api';
import type { Email, EmailStatus } from '../types';

// Stats card component
function StatCard({
  label,
  value,
  icon: Icon,
  trend,
  trendLabel,
  className = '',
}: {
  label: string;
  value: string | number;
  icon: typeof Mail;
  trend?: 'up' | 'down' | 'neutral';
  trendLabel?: string;
  className?: string;
}) {
  return (
    <Card className={className}>
      <CardContent padding="md">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-[var(--asi-text-secondary)]">{label}</p>
            <p className="text-2xl font-bold text-[var(--asi-text)] mt-1">{value}</p>
            {trendLabel && (
              <p
                className={`text-xs mt-1 ${
                  trend === 'up'
                    ? 'text-[var(--asi-success)]'
                    : trend === 'down'
                    ? 'text-[var(--asi-error)]'
                    : 'text-[var(--asi-text-muted)]'
                }`}
              >
                {trendLabel}
              </p>
            )}
          </div>
          <div className="p-3 bg-[var(--asi-bg-tertiary)] rounded-xl">
            <Icon className="w-6 h-6 text-[var(--asi-text-secondary)]" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Simple bar chart for timeline
function EmailChart({ data }: { data: { date: string; sent: number; opened: number; clicked: number }[] }) {
  if (!data || data.length === 0) {
    return (
      <div className="h-32 flex items-center justify-center text-[var(--asi-text-muted)]">
        No data available
      </div>
    );
  }

  const maxValue = Math.max(...data.map((d) => d.sent || 1));

  return (
    <div className="h-32 flex items-end gap-1">
      {data.slice(-30).map((day) => {
        const sentHeight = (day.sent / maxValue) * 100;
        const openedHeight = (day.opened / maxValue) * 100;
        const clickedHeight = (day.clicked / maxValue) * 100;

        return (
          <div
            key={day.date}
            className="flex-1 flex flex-col items-center justify-end gap-px group relative"
            title={`${format(new Date(day.date), 'MMM d')}: ${day.sent} sent, ${day.opened} opened, ${day.clicked} clicked`}
          >
            {/* Clicked bar */}
            {clickedHeight > 0 && (
              <div
                className="w-full bg-[#6D28D9] rounded-t"
                style={{ height: `${clickedHeight}%`, minHeight: clickedHeight > 0 ? '2px' : 0 }}
              />
            )}
            {/* Opened bar */}
            {openedHeight > clickedHeight && (
              <div
                className="w-full bg-[#7C3AED]"
                style={{ height: `${openedHeight - clickedHeight}%`, minHeight: '2px' }}
              />
            )}
            {/* Sent bar */}
            {sentHeight > openedHeight && (
              <div
                className="w-full bg-[#EDE9FE] rounded-t"
                style={{ height: `${sentHeight - openedHeight}%`, minHeight: '2px' }}
              />
            )}
            {/* Hover tooltip */}
            <div className="absolute bottom-full mb-2 hidden group-hover:block z-10">
              <div className="bg-[var(--asi-primary)] text-[var(--asi-text-light)] text-[10px] px-2 py-1 rounded whitespace-nowrap">
                {format(new Date(day.date), 'MMM d')}: {day.sent}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function Emails() {
  const [selectedEmailId, setSelectedEmailId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<EmailStatus | ''>('');

  // Fetch email stats
  const { data: stats } = useQuery({
    queryKey: ['emails', 'stats'],
    queryFn: () => emailsApi.stats(),
  });

  // Fetch timeline data
  const { data: timeline } = useQuery({
    queryKey: ['emails', 'timeline'],
    queryFn: () => emailsApi.statsTimeline(30),
  });

  // Fetch emails list
  const { data: emailsData, isLoading } = useQuery({
    queryKey: ['emails', 'list', statusFilter],
    queryFn: () =>
      emailsApi.list({
        status: statusFilter || undefined,
        direction: 'outbound',
        limit: 50,
      }),
  });

  const emails = (emailsData?.emails || []) as Email[];

  // Filter emails by search
  const filteredEmails = emails.filter((email) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      email.subject?.toLowerCase().includes(query) ||
      email.to_address?.toLowerCase().includes(query) ||
      email.contact_name?.toLowerCase().includes(query)
    );
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--asi-text)]">Emails</h1>
          <p className="text-[var(--asi-text-secondary)] mt-1">
            Track email outreach and engagement
          </p>
        </div>
        <Button disabled>
          <Mail className="w-4 h-4" />
          Compose Email
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Total Sent"
          value={stats?.total_sent || 0}
          icon={Send}
        />
        <StatCard
          label="Delivery Rate"
          value={`${stats?.delivery_rate || 0}%`}
          icon={CheckCheck}
          trend={stats?.delivery_rate && stats.delivery_rate > 95 ? 'up' : 'neutral'}
        />
        <StatCard
          label="Open Rate"
          value={`${stats?.open_rate || 0}%`}
          icon={Eye}
          trend={stats?.open_rate && stats.open_rate > 30 ? 'up' : 'neutral'}
        />
        <StatCard
          label="Click Rate"
          value={`${stats?.click_rate || 0}%`}
          icon={MousePointer}
          trend={stats?.click_rate && stats.click_rate > 10 ? 'up' : 'neutral'}
        />
      </div>

      {/* Chart */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Email Activity (30 days)</CardTitle>
            <div className="flex items-center gap-4 text-xs">
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded bg-[#EDE9FE]" />
                <span className="text-[var(--asi-text-secondary)]">Sent</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded bg-[#7C3AED]" />
                <span className="text-[var(--asi-text-secondary)]">Opened</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded bg-[#6D28D9]" />
                <span className="text-[var(--asi-text-secondary)]">Clicked</span>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <EmailChart data={timeline?.timeline || []} />
        </CardContent>
      </Card>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Email List */}
        <div className="lg:col-span-2 space-y-4">
          {/* Filters */}
          <div className="flex items-center gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--asi-text-muted)]" />
              <input
                type="text"
                placeholder="Search emails..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-[var(--asi-bg-secondary)] border border-[var(--asi-bg-tertiary)] rounded-xl text-sm text-[var(--asi-text)] placeholder:text-[var(--asi-text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--asi-accent)]"
              />
            </div>
            <div className="relative">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as EmailStatus | '')}
                className="appearance-none pl-4 pr-10 py-2 bg-[var(--asi-bg-secondary)] border border-[var(--asi-bg-tertiary)] rounded-xl text-sm text-[var(--asi-text)] focus:outline-none focus:ring-2 focus:ring-[var(--asi-accent)]"
              >
                <option value="">All Status</option>
                <option value="sent">Sent</option>
                <option value="delivered">Delivered</option>
                <option value="opened">Opened</option>
                <option value="clicked">Clicked</option>
                <option value="bounced">Bounced</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--asi-text-muted)] pointer-events-none" />
            </div>
          </div>

          {/* Email Cards */}
          <Card>
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--asi-accent)]" />
              </div>
            ) : filteredEmails.length === 0 ? (
              <div className="text-center py-12">
                <Mail className="w-12 h-12 mx-auto text-[var(--asi-text-muted)] mb-4" />
                <p className="text-[var(--asi-text-secondary)]">No emails found</p>
              </div>
            ) : (
              <div className="divide-y divide-[var(--asi-bg-tertiary)]">
                {filteredEmails.map((email) => (
                  <button
                    key={email.id}
                    onClick={() => setSelectedEmailId(email.id)}
                    className="w-full px-6 py-4 text-left hover:bg-[var(--asi-bg-secondary)] transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <EmailStatusBadge status={email.status} size="sm" />
                          <span className="text-xs text-[var(--asi-text-muted)]">
                            {email.sent_at
                              ? formatDistanceToNow(new Date(email.sent_at), { addSuffix: true })
                              : format(new Date(email.created_at), 'MMM d')}
                          </span>
                        </div>
                        <h3 className="font-medium text-[var(--asi-text)] truncate">
                          {email.subject || '(No subject)'}
                        </h3>
                        <p className="text-sm text-[var(--asi-text-secondary)] truncate mt-0.5">
                          To: {email.contact_name || email.to_address}
                          {email.contact_company && ` (${email.contact_company})`}
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Awaiting Response Sidebar */}
        <div className="lg:col-span-1">
          <AwaitingResponseList
            onContactClick={(contactId) => {
              // Could navigate to contact or filter emails
              console.log('Contact clicked:', contactId);
            }}
          />
        </div>
      </div>

      {/* Email Detail Drawer */}
      <EmailDetailDrawer
        emailId={selectedEmailId}
        onClose={() => setSelectedEmailId(null)}
      />
    </div>
  );
}
